import React, { FC, useEffect, useRef, useState } from 'react';

import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { Button, ConfigProvider, message } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { Auth } from 'aws-amplify';

import i18n from '@/content';
import { Loading } from '../../Common/Loading';
import SignIn from '../SignIn';
import SignUp from '../SignUp';
import ForgotPassword from '../ForgotPassword';
import ConfirmUser from '../ConfirmUser';
import { PublicFooter, PublicHeader } from '../../Common/PublicComponents';
import OTPSignInGuard from '../OTPSignIn/OTPSignInGuard';
import { withRouter, WithRouterProps } from '@/components/HOC';
import { CognitoAuthError, CognitoErrorCodes } from '@/types/types';
import { IAuthenticatorChildrenProps, SignInOptions } from '../interfaces';
import { authErrorMessageToI18nPayload, captureCognitoError, onSpacePress, tryGetTimelyRedirectPath, checkIsSpecificCode } from '../../utils';
import { trySignUp } from '@/components/hooks/useOTPFlow/helpers';
import './AuthContainer.scss';
import { useStores, useLoginMethodPreference, LOGIN_METHOD_OTP } from '@/components/hooks';
import useComponentDidUpdate from '../../hooks/useComponentDidUpdate';
import { PageLanguageContextProvider } from '@/components/MiniApps/PageLanguageContextProvider';
import LanguageSwitcher from '@/components/MiniApps/Common/LanguageSwitcher';
import { MAP_I18N_LANGUAGE_TO_ANT_LOCALE } from '@/content/languages';

import styles from './AuthContainer.module.scss';
import appConfig from '@/config/env';

const emailConfirmationNameSpace = 'emailConfirmation';
export const LONG_MESSAGE_DURATION_SECONDS = 60;

export interface AuthContainerProps extends IAuthenticatorChildrenProps, WithRouterProps {}

const AuthContainer: FC<IAuthenticatorChildrenProps> = (props) => {
    const { appStore, authSettingsStore } = useStores();
    const { t, i18n: i18nInstance } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { setLoginMethodPreference } = useLoginMethodPreference();

    const [isNotConfirmed, setIsNotConfirmed] = useState<boolean>(false);
    const [confirmationUsername, setConfirmationUsername] = useState<string>('');
    const [userName, setUserName] = useState<string>('');
    const [userPassword, setUserPassword] = useState<string>('');
    const [isCreateNewPasswordStep, setIsCreateNewPasswordStep] = useState<boolean>(false);

    useComponentDidUpdate(() => {
        const { setErrorStatus } = appStore;
            setErrorStatus(false);
            setIsNotConfirmed(false);
            setConfirmationUsername('');
        }, [location]);

    useEffect(() => {
        return () => {
            appStore.setErrorStatus(false);
        }
    }, []);

    const clickConfirm = async (): Promise<void> => {
        const { appStore: { setLoadingStatus, setErrorStatus } } = props;
        try {
            setLoadingStatus(true, i18n.t(`${emailConfirmationNameSpace}.sendingCode`));
            await Auth.resendSignUp(confirmationUsername);
            setErrorStatus(false);
            setIsNotConfirmed(false);
            setConfirmationUsername('');
            message.success(i18n.t(`${emailConfirmationNameSpace}.pleaseCheckEmail`));
        } catch (error) {
            captureCognitoError(error, 'AuthContainer.clickConfirm');
            message.error(i18n.t(`${emailConfirmationNameSpace}.couldNotSend`, { confirmationUsername }));
        } finally {
            setLoadingStatus(false);
        }
    }

    const signInCalls = async (
        authWorker: () => Promise<any>,
        nameSpace: string,
        options: SignInOptions,
    ): Promise<boolean> => {
        const { username, isFederated, password } = options;
        const { setLoadingStatus, setErrorStatus } = appStore;

        try {
            setIsNotConfirmed(false);
            setConfirmationUsername('');
            setErrorStatus(false);
            setLoadingStatus(true, i18n.t(`${nameSpace}.loading`));
            const user = await authWorker();
            if (user && nameSpace === 'signUp') {
                message.success(i18n.t(`${emailConfirmationNameSpace}.emailSent`), LONG_MESSAGE_DURATION_SECONDS);
            }
            // We shouldn't stop loading for federated sign in, because in this case we got response before redirect
            if (!isFederated) {
                setLoadingStatus(false);
                setErrorStatus(false);
            }
            return true;
        } catch (error) {
            captureCognitoError(error, 'AuthContainer.signInCalls');
            const { code: errorCode, message: cognitoErrorMessage } = error as CognitoAuthError;
            let errorMessage = '';
            if (errorCode === CognitoErrorCodes.UsernameExistsException || checkIsSpecificCode(error, 409)) {
                setUserName(username);
                setUserPassword(password);
                await Auth.forgotPassword(username.toLowerCase());
                navigate('/confirmUser')
                setLoadingStatus(false);
                return null;
            }
            if (errorCode === CognitoErrorCodes.UserNotFoundException && nameSpace.startsWith('forgotPassword')) {
                // Avoid /signUp bounce for entity-only recipients (no Cognito user yet).
                try {
                    await trySignUp(username.toLowerCase(), authSettingsStore.API);
                    await Auth.forgotPassword(username.toLowerCase());
                    setLoadingStatus(false);
                    setErrorStatus(false);
                    return true;
                } catch (retryError) {
                    captureCognitoError(retryError, 'AuthContainer.signInCalls.silentSignUpRetry');
                    navigate('/signUp', { state: { prefillEmail: username } });
                    message.info(i18n.t('forgotPassword.userNotFoundRedirectToSignUp'));
                    setLoadingStatus(false);
                    return null;
                }
            }
            if (errorCode === CognitoErrorCodes.UserNotConfirmedException) {
                setIsNotConfirmed(true);
                setConfirmationUsername(username);
            } else {
                const [key, i18nOption] = authErrorMessageToI18nPayload(cognitoErrorMessage, () => {
                    const i18nErrorCode = nameSpace === 'signIn' ? `${errorCode}SignIn` : errorCode;
                    const errorType = i18n.exists(`auth.errors.${i18nErrorCode}`)
                        ? i18nErrorCode
                        : 'serverIssue';
                    return [`auth.errors.${errorType}`];
                });
                errorMessage = i18n.t(key, i18nOption);
            }

            if (nameSpace !== 'Otp') {
                setErrorStatus(true, '', errorMessage || cognitoErrorMessage);
            }
            setLoadingStatus(false);

            return false;
        }
    };

    const { errorStatus, loadingStatus } = appStore;
    const { hasError, errorMessage } = errorStatus;

    const baseSignInPageProps = {
        signInCalls,
        location,
        navigate,
        ...props
    };

    const locale = MAP_I18N_LANGUAGE_TO_ANT_LOCALE[i18nInstance.language];

    // Check if user came from recipient page; stick this so the header button does not disappear on re-render
    const savedRedirectPath = tryGetTimelyRedirectPath();
    const isFromRecipientPage = savedRedirectPath &&
        (savedRedirectPath.includes('/recipient/') || savedRedirectPath.includes('/shared-file'));

    const showLoginWithCodeRef = useRef<boolean>(false);
    const savedRedirectPathRef = useRef<string | null>(null);
    if (isFromRecipientPage) {
        showLoginWithCodeRef.current = true;
        if (savedRedirectPath) {
            savedRedirectPathRef.current = savedRedirectPath;
        }
    }
    const showLoginWithCodeButton = showLoginWithCodeRef.current;

    const handleBackToRecipientPage = () => {
        setLoginMethodPreference(LOGIN_METHOD_OTP);

        const pathToNavigate = savedRedirectPathRef.current ?? tryGetTimelyRedirectPath();
        if (pathToNavigate && (pathToNavigate.includes('/recipient/') || pathToNavigate.includes('/shared-file'))) {
            navigate(pathToNavigate);
        }
    };

    return (
        <PageLanguageContextProvider>
            <ConfigProvider direction={i18nInstance.dir()} locale={locale}>
                <div className="auth-container">
                    <div className="root-auth-container">
                        <PublicHeader>
                            <div className={styles['language-switcher-container']}>
                                {showLoginWithCodeButton && !isCreateNewPasswordStep && !appConfig.HIDE_LOGIN_WITH_CODE && (
                                    <Button
                                        onClick={handleBackToRecipientPage}
                                        className={styles['login-with-code-button']}
                                    >
                                        {i18nInstance.t('signIn.loginWithCode')}
                                    </Button>
                                )}
                                <LanguageSwitcher />
                            </div>
                        </PublicHeader>
                        <div className="content-wrapper">
                            {loadingStatus.isLoading && <Loading fullScreen loadingText={t('general.waitMessages.pleaseWait')} />}
                            <Routes>
                        <Route
                            path="/signIn"
                            element={<SignIn {...baseSignInPageProps} />}
                        />
                        <Route
                            path="/gdrive/share"
                            element={<SignIn gdrive {...baseSignInPageProps} />}
                        />
                        <Route
                            path="/box/share"
                            element={<SignIn box {...baseSignInPageProps} />}
                        />
                        <Route
                            path="/signUp"
                            element={<SignUp signInCalls={signInCalls} location={location} navigate={navigate} authSettingsStore={authSettingsStore} {...props} />}
                        />
                        <Route
                            path="/forgotPassword"
                            element={<ForgotPassword signInCalls={signInCalls} onCreateNewPasswordStepChange={setIsCreateNewPasswordStep} />}
                        />
                        <Route
                            path="/OTPSignIn"
                            element={<OTPSignInGuard />}
                        />
                        <Route
                            path="/confirmUser"
                            element={<ConfirmUser userName={userName} userPassword={userPassword}/>}
                        />
                        <Route
                            path="*"
                            element={(
                                <Navigate
                                    to={{ search: window.location.search, pathname: '/signIn' }}
                                    replace
                                />
                            )}
                        />
                    </Routes>
                    {hasError && (
                        <div className="error-container">
                            {isNotConfirmed
                                ? (
                                    <Trans i18nKey="auth.errors.notConfirmed">
                                        Please confirm email
                                        <span
                                            role="button"
                                            tabIndex={0}
                                            key="check"
                                            className="confirmation"
                                            onClick={clickConfirm}
                                            onKeyUp={(event) => onSpacePress(event, clickConfirm)}
                                        >
                                                Resend code
                                            </span>
                                        if you have not got one
                                    </Trans>
                                )
                                : <span>{errorMessage}</span>}
                        </div>
                    )}
                </div>
                <PublicFooter />
            </div>
        </div>
            </ConfigProvider>
        </PageLanguageContextProvider>
    );
};
export default AuthContainer;
